import { useEffect, useState } from "react";
import { X, Download, Share, Plus, Wrench } from "lucide-react";

const DISMISS_KEY = "ironlog_install_dismissed";

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;

    const ua = window.navigator.userAgent || "";
    const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    if (ios) {
      setIsIOS(true);
      setShow(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") setShow(false);
    setDeferred(null);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-md animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-[#1A1C23] border border-[#FFB800]/40 shadow-2xl shadow-black/60 rounded-lg p-4 flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 bg-[#FFB800] flex items-center justify-center rounded-md">
          <Wrench className="w-6 h-6 text-black" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-head font-700 text-white text-lg uppercase tracking-tight">Install IronLog</p>
          {isIOS ? (
            <p className="font-body text-sm text-[#94A3B8] mt-0.5">
              Tap <Share className="inline w-4 h-4 -mt-0.5 text-[#FFB800]" /> Share, then{" "}
              <span className="text-white">“Add to Home Screen”</span>{" "}
              <Plus className="inline w-4 h-4 -mt-0.5 text-[#FFB800]" /> for one-tap access.
            </p>
          ) : (
            <p className="font-body text-sm text-[#94A3B8] mt-0.5">
              Add IronLog to your home screen for full-screen, one-tap access on site.
            </p>
          )}
          {!isIOS && (
            <button
              onClick={install}
              className="btn-signal h-10 px-4 mt-3 font-head text-sm flex items-center gap-2"
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4" /> Install App
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-[#94A3B8] hover:text-white transition-colors shrink-0"
          aria-label="Dismiss"
          data-testid="pwa-dismiss-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
